import { ticks } from './config';
import { ROYALE } from './royale';

/**
 * Fog of war (ADR-0010). The alley is open ground everyone sees; in the jungle you only see a small circle
 * around you plus a cone towards where you aim, and walls (hedges, thickets, trees, rocks) block sight — mostly:
 * light seeps a little way through thin cover and round corners (a glimpse). Your team shares what it sees,
 * and what it saw lingers for a moment after it looks away. Enemies in the jungle that nobody on your team
 * sees (or saw a moment ago) are not sent to you at all — shooting at you or hitting you doesn't change that (ADR-0034).
 */
/**
 * The shape of a gardener's sight, walls aside: a bubble all round (`radius`, px) and a cone where it aims, half
 * `coneHalfAngle` (radians) wide each way, its reach tapering to the bubble across `coneSoftness` (radians, centred on the
 * half angle). Its length is the map's (`MapDef.cone`), times `reach` where it has one. `VISION` is a gardener's own; one
 * hiding in a bush has another (`ROYALE.bush.eye`, ADR-0193 amended), one holding a Sniper its `SCOPE`.
 */
export interface VisionShape {
  radius: number;
  coneHalfAngle: number;
  coneSoftness: number;
  reach?: number;
}

export const VISION = {
  /** Always-visible bubble around a player, px. */
  radius: 72,
  /** Reach of the aim cone, px (not much past the bubble: where you look matters, but less than where you are). */
  coneRange: 150,
  /** Half the cone's opening angle, radians (~30°). */
  coneHalfAngle: 0.52,
  /**
   * Width (radians) of the cone's soft side, centred on `coneHalfAngle`: the reach tapers from `coneRange`
   * down to `radius` across it (the fog renderer's light falls off with it; the server counts all of it).
   */
  coneSoftness: 0.4,
  /**
   * Glimpses: sight carries on past the point where it meets a wall for up to `glimpse` px, as long as the wall
   * it crosses is no thicker than `glimpseWall` px along the line (a lone rock or tree, a corner it clips) and
   * no second wall is in the way. Dim, and dimmer the more wall it went through.
   */
  glimpse: 44,
  glimpseWall: 28,
  /**
   * Shadows have soft edges: the fog renderer leaks light up to this many px sideways into a wall's shadow.
   * Must not exceed a player's hurt radius — the server lets you see an enemy whose edge peeks out, which is
   * what keeps everything in that soft edge on the wire.
   */
  penumbra: 5,
  /** How long a team keeps seeing an enemy after its sight left them (the fog closes gradually). */
  memoryTicks: ticks(1.2),
  /**
   * Extra reach (px) the server grants when deciding who may see whom, so enemies are already on the wire
   * when the fog opens over them despite latency and interpolation.
   */
  serverMargin: 16,
} as const;

/**
 * A Sniper's scope (`WeaponDef.scope`): only a little more sight than a gardener's own (the owner's call), its cone
 * reaching 1.25 times as far as the map's (on a battle royale's maps 281 px, from 225: a Sniper's view zooms out a little
 * and leans further, `JUICE.camera.scope`) and opening a little narrower; its bubble is a gardener's own.
 */
export const SCOPE: VisionShape = { radius: VISION.radius, coneHalfAngle: 0.4, coneSoftness: 0.3, reach: 1.25 };

/**
 * A car's driver's sight (ADR-0214): along the car, whatever the crosshair does (`Aim.look` is its heading), a cone half
 * as wide again as a gardener's and reaching 1.42 times the map's (320 px on a battle royale's maps).
 */
export const DRIVE_EYE: VisionShape = { radius: VISION.radius, coneHalfAngle: 0.8, coneSoftness: 0.45, reach: 1.42 };

/**
 * The shape of the sight of a gardener holding `weapon`: at a car's wheel (`driving`) the car's, in a bush (`bushed`) its
 * leaves', else its scope's, else its own.
 */
export const eyeOf = (weapon: { readonly scope?: true }, bushed: boolean, driving = false): VisionShape =>
  driving ? DRIVE_EYE : bushed ? ROYALE.bush.eye : weapon.scope ? SCOPE : VISION;

/** How far a cone of `shape` reaches on a map whose cone is `cone` px long (`MapDef.cone`). */
export const coneOf = (cone: number, shape?: VisionShape): number => cone * (shape?.reach ?? 1);

/**
 * Pings (ADR-0069): a right click on the minimap marks a spot for your team (the `signal` order and event). A player gets
 * at most `burst` within `windowTicks`; the rest are dropped — each one reaches the whole team and plays a sound, so a
 * flood would deafen it.
 */
export const SIGNAL = {
  burst: 3,
  windowTicks: ticks(3),
} as const;
